import { Typography } from "@/components/atoms/Typography";
import { DownloadIcon, FileIcon } from "@/components/atoms/icons";
import { useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import React from "react";
import {
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
  type StyleProp,
  type ViewStyle,
} from "react-native";

export interface DocumentItemProps {
  id: string;
  title: string;
  type: string;
  downloadUrl: string;
  onPress?: () => void;
  /** Optional overrides (e.g. padding) for the bordered row box. */
  containerStyle?: StyleProp<ViewStyle>;
  /** Optional overrides (e.g. gap) for the icon/title/icon row. */
  contentStyle?: StyleProp<ViewStyle>;
}

export function DocumentItem({
  id,
  title,
  type,
  downloadUrl,
  onPress,
  containerStyle,
  contentStyle,
}: DocumentItemProps) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();

  const handlePress = () => {
    if (onPress) {
      onPress();
    }
  };

  return (
    <TouchableOpacity
      style={[
        styles.container,
        {
          backgroundColor: colors.backgroundLevel1, // White background
          borderColor: colors.backgroundLevel4, // Light grey border
        },
        containerStyle,
      ]}
      onPress={handlePress}
      activeOpacity={0.7}
    >
      <View style={[styles.content, contentStyle]}>
        {/* File Icon */}
        <FileIcon size={20} color={colors.fontPrimary} />

        {/* Document Title */}
        {/* Explicit RTL alignment is web-only (same as Typography): native's
            root `direction: 'rtl'` already mirrors 'left' to the right edge,
            so forcing 'right' there double-flips Arabic back to the left. */}
        <Typography
          variant="body"
          color="primary"
          style={[
            styles.documentTitle,
            Platform.OS === "web" && { textAlign: isRTL ? "right" : "left" },
          ]}
        >
          {title}
        </Typography>

        {/* Download Icon */}
        <DownloadIcon size={20} color={colors.fontPrimary} />
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
  },
  content: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  documentTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: "600",
  },
});

export default DocumentItem;
